import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    obterConfiguracaoFiscal,
    salvarConfiguracaoFiscal,
} from "../../../services/configuracaoFiscal"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { ConfiguracaoFiscalInicial } from "../models/configuracaoFiscalModel"
import { ConfiguracaoFiscalActionsTypes } from "./configuracaoFiscalActions"
import { ConfiguracaoFiscalContext } from "./ConfiguracaoFiscalContext"
import { configuracaoFiscalReducer } from "./configuracaoFiscalReducer"

export function ConfiguracaoFiscalContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(configuracaoFiscalReducer, ConfiguracaoFiscalInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await obterConfiguracaoFiscal(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar configuração fiscal")
            } else {
                setErroCarregamento(null)
                dispatch({
                    type: ConfiguracaoFiscalActionsTypes.CARREGAR,
                    payload: {
                        emissaoAutomatica: response.emissaoAutomatica,
                        cnpjPlugNotas: response.cnpjPlugNotas ?? "",
                        tipoEmissao: response.tipoEmissao,
                        limiteDiario:
                            response.limiteDiario != null ? String(response.limiteDiario) : "",
                        emailsFechamentoFiscal: response.emailsFechamentoFiscal ?? "",
                        emissoesHoje: response.emissoesHoje,
                        formas: response.formas,
                    },
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar configuração fiscal")
        } finally {
            setLoading(false)
        }
    }

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const limiteDiarioNum =
                dados.limiteDiario !== "" ? parseInt(dados.limiteDiario, 10) : null
            const payload = {
                emissaoAutomatica: dados.emissaoAutomatica,
                cnpjPlugNotas: dados.cnpjPlugNotas.trim() || null,
                tipoEmissao: dados.tipoEmissao,
                limiteDiario:
                    limiteDiarioNum != null && limiteDiarioNum > 0 ? limiteDiarioNum : null,
                emailsFechamentoFiscal: dados.emailsFechamentoFiscal.trim() || null,
                idsSelecionados: dados.formas.filter(f => f.selecionada).map(f => f.id),
            }
            const response = await salvarConfiguracaoFiscal(autorizacao, payload)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
        } finally {
            setLoading(false)
        }
    }, [dados, setLoading])

    useEffect(() => {
        carregar()
    }, [])

    return (
        <ConfiguracaoFiscalContext.Provider value={{ dados, dispatch, erroCarregamento, salvar }}>
            <Loading />
            {children}
        </ConfiguracaoFiscalContext.Provider>
    )
}
